import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import {
  mapRoomToCreateRequest,
  mapRoomToUpdateRequest,
} from 'app/hooks/data/core/admin/rooms/mapping/form';
import { useCreateRoom } from 'app/hooks/data/core/admin/rooms/useCreateRoom';
import { useDeleteRoom } from 'app/hooks/data/core/admin/rooms/useDeleteRoom';
import { useUpdateRoom } from 'app/hooks/data/core/admin/rooms/useUpdateRoom';
import { useSaveRoom } from 'app/hooks/data/rooms/useRoom';
import type { IHotel, Room } from 'app/typings';
import { isDeepEqual } from 'app/utils/object';

type UseRoomActionsProps = {
  roomId?: number;
  isCreatePage: boolean;
  fetchedRoom?: Room;
  hotels?: IHotel[];
  initialValues?: Partial<Room>;
};

export const useRoomActions = ({
  roomId,
  isCreatePage,
  fetchedRoom,
  hotels,
  initialValues,
}: UseRoomActionsProps) => {
  const [form] = Form.useForm<Room>();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { mode, enterViewMode, enterEditMode } =
    useContext(ViewEditModeContext);

  const { mutateAsync: savePackageRoom } = useSaveRoom();
  const { mutateAsync: createOfferRoom } = useCreateRoom();
  const { mutateAsync: updateOfferRoom } = useUpdateRoom();
  const { mutateAsync: deleteRoom } = useDeleteRoom();

  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isCreatePage) {
      enterEditMode();
    }
  }, [isCreatePage, enterEditMode]);

  const onFinish = useCallback(
    async (values: Room) => {
      try {
        const formattedValues = {
          ...initialValues,
          ...values,
          pictures: values.pictures || [],
          hasPRM: values.hasPRM || false,
        };

        if (isCreatePage) {
          const isLegacyHotel = hotels?.find(
            (h) => h.id === values.hotelId
          )?.isLegacy;

          const createdRoom = !isLegacyHotel
            ? await createOfferRoom(mapRoomToCreateRequest(formattedValues))
            : await savePackageRoom(formattedValues);

          const path =
            !isLegacyHotel && typeof createdRoom === 'object'
              ? `/rooms/${createdRoom.id}`
              : `/rooms/legacy/${createdRoom}`;

          history.push(path);
        } else if (roomId) {
          const payload = mapRoomToUpdateRequest(formattedValues);

          const hasChanged =
            !fetchedRoom ||
            !isDeepEqual(payload, mapRoomToUpdateRequest(fetchedRoom));

          if (hasChanged) {
            await updateOfferRoom({
              id: roomId,
              payload,
            });
            queryClient.invalidateQueries(['room', roomId]);
          }
        } else {
          return;
        }

        enterViewMode();
      } finally {
        setIsLoading(false);
      }
    },
    [
      initialValues,
      isCreatePage,
      roomId,
      enterViewMode,
      fetchedRoom,
      hotels,
      createOfferRoom,
      savePackageRoom,
      history,
      updateOfferRoom,
      queryClient,
    ]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    enterViewMode();
  }, [form, enterViewMode]);

  const handleDelete = useCallback(async () => {
    if (roomId) {
      await deleteRoom(roomId);
      history.push('/rooms');
    }
  }, [deleteRoom, roomId, history]);

  const handleSave = useCallback(() => {
    form.submit();
    setIsLoading(true);
  }, [form]);

  return {
    form,
    mode,
    canSubmit,
    isLoading,
    onFinish,
    onCancel,
    handleDelete,
    handleSave,
    enterEditMode,
    setCanSubmit,
  };
};
